import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PhoneOutgoing, Loader2, Smartphone, Delete, Headphones } from 'lucide-react';
import { apuntarA, soportaCentralita } from '@/components/centralita/AnclaCCP';
import { useCentralita, llamar, normalizar } from '@/hooks/useCentralita';
import EstadoAgente from '@/components/centralita/EstadoAgente';
import Agenda from '@/components/centralita/Agenda';

/** Sección propia, no pestaña: por eso lleva su cabecera. */
function Cabecera() {
  return (
    <div className="flex items-center gap-3 mb-4 sm:mb-6">
      <div className="bg-emerald-600 p-2 sm:p-2.5 rounded-xl text-white shrink-0">
        <Headphones size={20} />
      </div>
      <div className="min-w-0">
        <h1 className="text-xl sm:text-2xl font-bold text-sereno-dark">Centralita</h1>
        <p className="text-slate-500 text-xs sm:text-sm mt-0.5">
          Llama desde el CRM. Lo que hables queda en{' '}
          <Link to="/llamadas" className="underline underline-offset-2 hover:text-slate-700">Llamadas</Link>,
          grabado y transcrito igual que lo que entra.
        </p>
      </div>
    </div>
  );
}

/**
 * La centralita.
 *
 * No contiene el panel de Amazon Connect: contiene el hueco donde el panel se
 * coloca. El iframe vive anclado al layout raíz (ver AnclaCCP) porque
 * desmontarlo cortaría la llamada en curso, así que esta página solo registra
 * su hueco al entrar y lo libera al salir.
 */
export default function CentralitaPage() {
  const hueco = useRef<HTMLDivElement>(null);
  const central = useCentralita();
  const [marcado, setMarcado] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const soportado = soportaCentralita();

  useEffect(() => {
    if (!soportado) return;
    apuntarA(hueco.current);
    return () => apuntarA(null);
  }, [soportado]);

  const marcar = async () => {
    const e164 = normalizar(marcado);
    if (!e164) return setAviso('Escribe un número válido, por ejemplo 600 11 22 33.');
    setEnviando(true);
    setAviso(null);
    try {
      await llamar(e164);
      setMarcado('');
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'No se pudo llamar.');
    } finally {
      setEnviando(false);
    }
  };

  if (!soportado) {
    return (
      <div className="max-w-6xl mx-auto">
        <Cabecera />
        <div className="card text-center py-12 px-5">
          <Smartphone className="mx-auto text-slate-300 dark:text-slate-600 mb-3" size={32} />
          <p className="text-slate-600 dark:text-slate-300 font-medium">
            La centralita no funciona en este navegador
          </p>
          <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">
            El teléfono de Amazon Connect necesita Chrome, Edge o Firefox de escritorio. Ni Safari ni
            los navegadores de móvil soportan el audio que usa. El resto del CRM sí funciona aquí.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <Cabecera />
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,340px)_1fr] gap-4 items-start">
        {/* El hueco. El panel de Connect se coloca encima; lo de dentro solo se
            ve mientras el panel aún no ha aparecido. */}
        <div
          ref={hueco}
          className="h-[600px] rounded-xl border border-dashed border-slate-200 dark:border-white/10 grid place-items-center"
        >
          {central.estado === 'cargando' && (
            <div className="text-center text-slate-400">
              <Loader2 className="animate-spin mx-auto mb-2" size={22} />
              <p className="text-sm">Conectando con la centralita…</p>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <EstadoAgente />

          <div className="card">
            <p className="label mb-2">Marcar un número</p>
            <div className="flex gap-2">
              <input
                className="input flex-1"
                inputMode="tel"
                placeholder="600 11 22 33"
                value={marcado}
                onChange={(e) => { setMarcado(e.target.value); setAviso(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') marcar(); }}
              />
              {marcado && (
                <button onClick={() => setMarcado('')} className="btn-ghost px-3" aria-label="Borrar">
                  <Delete size={16} />
                </button>
              )}
              <button
                onClick={marcar}
                disabled={enviando || central.estado !== 'libre'}
                className="btn-primary flex items-center gap-1.5 px-4 disabled:opacity-40"
              >
                {enviando ? <Loader2 className="animate-spin" size={16} /> : <PhoneOutgoing size={16} />}
                Llamar
              </button>
            </div>
            {/* Los nueve dígitos españoles se completan solos, pero un número
                internacional hay que escribirlo con su prefijo. */}
            <p className="text-xs text-slate-400 mt-2">
              Nueve dígitos para España. Para otros países, con prefijo: +351…
            </p>
            {aviso && <p className="text-sm text-amber-700 dark:text-amber-300 mt-2">{aviso}</p>}
            {central.error && <p className="text-sm text-rose-700 dark:text-rose-300 mt-2">{central.error}</p>}
          </div>

          <Agenda />

          <p className="text-xs text-slate-400">
            Las llamadas salen desde el +34 919 019 716.
          </p>
        </div>
      </div>
    </div>
  );
}
